import React, { useMemo, useState, useEffect } from "react";
import { BsFillArrowLeftCircleFill, BsChevronDown } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  toggleJobsheet,
  toggleOrders,
  toggleQuote,
  toggleStore,
  toggleDocs,
  toggleDiecut,
  toggleProd,
  toggleUser,
} from "../features/PageSlice";
import { setPermissions } from "../features/authSlice";

import logo from "../elkom.png";
import { NavLink } from "react-router-dom";
import LoginStatus from "../components/LoginStatus";
import { MENU } from "../pages/menuItems";

const SECTION_TOGGLES = {
  quotation: { openFlag: "subQuote", action: toggleQuote },
  jobsheet: { openFlag: "subJobsheet", action: toggleJobsheet },
  orders: { openFlag: "subOrders", action: toggleOrders },
  documents: { openFlag: "subDocs", action: toggleDocs },
  diecut: { openFlag: "subDiecut", action: toggleDiecut },
  production: { openFlag: "subProd", action: toggleProd },
  store: { openFlag: "subStore", action: toggleStore },
  user: { openFlag: "subUser", action: toggleUser },
};

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const { user, permissions } = useSelector((state) => state.auth);
  const pageState = useSelector((state) => state.pages);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Fetch permissions once per login session (stored in Redux)
  useEffect(() => {
    if (!user || permissions !== null) return;
    if (user.user_lvl === 1) {
      dispatch(setPermissions("admin"));
      return;
    }
    axios
      .get(
        `https://eps.elkomprintama.com/get/api/users/permissions/${user.user_lvl}`,
      )
      .then((res) => dispatch(setPermissions(res.data.permissions)))
      .catch(() => dispatch(setPermissions([])));
  }, [user, permissions, dispatch]);

  const permSet = useMemo(() => {
    if (!permissions || permissions === "admin") return null;
    return new Set(permissions);
  }, [permissions]);

  const canSee = (item) => {
    if (!user) return false;
    if (user.user_lvl === 1 || permissions === "admin") return true;
    if (permissions === null) return false; // still loading

    if (item.children) {
      return item.children.some((ch) => permSet?.has(ch.key));
    }
    return permSet?.has(item.key) ?? false;
  };

  const visibleMenu = useMemo(
    () => MENU.filter((m) => canSee(m)),
    [user, permissions, permSet],
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Top bar */}
      <div
        className="text-white px-5 flex justify-between items-center shadow-lg z-10 flex-shrink-0"
        style={{
          background:
            "linear-gradient(135deg, #0f2027 0%, #203a43 60%, #2c5364 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          minHeight: "56px",
        }}
      >
        {/* Left: logo + title */}
        <div className="flex items-center gap-3">
          <div className="bg-white/10 rounded-lg p-1 flex-shrink-0">
            <img
              src={logo}
              width="36"
              height="36"
              alt="logo"
              className="object-contain"
            />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight tracking-wide">
              EPS
            </p>
            <p className="text-blue-300 text-[10px] leading-tight opacity-80 whitespace-nowrap">
              PT. Elkom Printama Batam
            </p>
          </div>
          {/* Accent divider */}
          <div className="h-6 w-px bg-white/15 ml-2 hidden sm:block" />
          <p className="text-gray-400 text-[10px] hidden sm:block">
            Elkom Production System
          </p>
        </div>
        <LoginStatus />
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div
          className={`bg-gray-800 flex-shrink-0 flex flex-col relative transition-all duration-300 ${
            sidebarOpen ? "w-56" : "w-16"
          }`}
        >
          <BsFillArrowLeftCircleFill
            className={`bg-white text-gray-800 text-2xl rounded-full absolute -right-3 top-6 cursor-pointer z-20 transition-transform duration-300 ${
              !sidebarOpen ? "rotate-180" : ""
            }`}
            onClick={() => setSidebarOpen((v) => !v)}
          />

          <ul className="pt-3 pb-6 overflow-y-auto flex-1">
            {visibleMenu.map((item) => {
              const Icon = item.icon;
              const hasChildren =
                Array.isArray(item.children) && item.children.length > 0;
              const toggleInfo = SECTION_TOGGLES[item.key];
              const isOpen = toggleInfo
                ? pageState[toggleInfo.openFlag]
                : false;

              const visibleChildren = hasChildren
                ? item.children.filter((ch) => canSee(ch) && !ch.action)
                : [];

              return (
                <li key={item.key}>
                  {!hasChildren && item.to ? (
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 mx-2 my-0.5 rounded-md text-sm transition-colors ${
                          isActive
                            ? "bg-blue-600 text-white font-semibold"
                            : "text-gray-300 hover:bg-gray-700 hover:text-white"
                        }`
                      }
                    >
                      {Icon && <Icon size="1.2em" className="flex-shrink-0" />}
                      {sidebarOpen && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </NavLink>
                  ) : (
                    <div
                      className="flex items-center gap-3 px-3 py-2 mx-2 my-0.5 rounded-md text-sm text-gray-300 hover:bg-gray-700 hover:text-white cursor-pointer select-none transition-colors"
                      onClick={() => {
                        if (toggleInfo) dispatch(toggleInfo.action());
                      }}
                    >
                      {Icon && <Icon size="1.2em" className="flex-shrink-0" />}
                      {sidebarOpen && (
                        <>
                          <span className="truncate flex-1">{item.label}</span>
                          <BsChevronDown
                            className={`flex-shrink-0 transition-transform duration-200 ${
                              isOpen ? "rotate-180" : ""
                            }`}
                          />
                        </>
                      )}
                    </div>
                  )}

                  {hasChildren && isOpen && sidebarOpen && (
                    <ul className="ml-5 border-l border-gray-600 pl-2 mb-1">
                      {visibleChildren.map((ch) => (
                        <li key={ch.key}>
                          <NavLink
                            to={ch.to}
                            end
                            className={({ isActive }) =>
                              `flex items-center px-3 py-1.5 my-0.5 rounded-md text-xs transition-colors ${
                                isActive
                                  ? "bg-blue-600 text-white font-semibold"
                                  : "text-gray-400 hover:bg-gray-700 hover:text-white"
                              }`
                            }
                          >
                            {ch.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Main content */}
        <div className="flex-1 bg-gray-100 overflow-auto p-4">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
